import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mentionize, urlize } from 'mk/bazaar/common/filters';
import { hashize } from 'mk/bazaar/common/filters/hashize';
import { reactFilter, TextLike } from 'mk/bazaar/common/filters/utils';
import { slugify } from 'mk/common/utils';
import { BASE_URL } from 'mk/settings';
import { blogsUrl } from 'mk2/apps/blogs/urls';
import { strollersDetailUrl, strollersListUrl } from 'mk2/apps/strollers/urls';
import { BrandLink, DetailLink } from 'mk2/apps/strollers/utils';
import WikiLink from 'mk2/apps/wiki/components/WikiLink';
import { AST, ASTInstagramData, ASTLinkedObjectsData, ASTLinkedObjectType } from 'mk2/apps/wiki/schemas';
import {
    constructGiphy,
    constructIframe,
    constructImage,
    constructInstagram,
    constructVimeo,
    constructYoutube,
    linkProcessor,
    LinkProcessorOpts,
    RenderSnippetAs,
} from 'mk2/apps/wiki/utils';
import Link from 'mk2/components/Link';
import { getAllDescendantsOfAType, getAttributesDict, removeQuotes } from 'mk2/helpers/article_ast/utils';
import { url } from 'mk2/helpers/urls';
import { PhotoEntity } from 'mk2/schemas';
import * as path2regex from 'path-to-regexp';
import React from 'react';

const RE_EXTRACT_ARTICLE_SLUG = /^(?:\/[^\/#]+)?\/([^\/#]+)\/(?:[?#].+)?$/;

const BOOLEAN_ATTRIBUTE__TRUE = 'BOOLTRUE';

type ReactChild = React.ReactChild;

export enum RenderLinkedObjectsAs {
    VARIATIONS_OF_CURRENT_SYNTAX,
    PLAIN_URL_LINK,
}

export interface FilterAdditionalData {
    countTopicContributors: {[topicSlug: string]: number};
    linkedObjects: ASTLinkedObjectsData;
    instagramData: ASTInstagramData;
    renderForEditor: boolean;
    photos: PhotoEntity[];
    photoStyle?: any;
    instagramStyle?: any;
    ignorePhotosAndVideos?: boolean;
    hasResponsiveImages: boolean; // if true then use ImgResponsive; else use Img
    forceEagerImages: boolean; // if true then set prop lazy (of Img/ImgResponsive) to false; else use default
    escapeMarkdownLikePartsOfPlainText: boolean;
    renderContributorsCount: boolean;
    renderSnippetObjectsAs: RenderSnippetAs;
    renderLinkedObjectsAs: RenderLinkedObjectsAs;
    renderSpecialWikiLinks: boolean;
    renderLinksWithNofollow: boolean;
    rootDiv: boolean;
    anchorHeadings: boolean;
    chevronLists: boolean;
    isMobile: boolean;

    selectPhotoVersionFn(photo: PhotoEntity): string;
    hashizeFn(bodyChunk: TextLike): React.ReactChild[];
    faFn(faName: string): React.ReactChild;
}

export const defaultFilterAdditionalData: FilterAdditionalData = {
    countTopicContributors: null,
    linkedObjects: null,
    instagramData: null,
    renderForEditor: false,
    photos: [],
    photoStyle: null,
    instagramStyle: null,
    ignorePhotosAndVideos: false,
    hasResponsiveImages: true,
    forceEagerImages: false,
    escapeMarkdownLikePartsOfPlainText: false,
    renderContributorsCount: false,
    renderSnippetObjectsAs: RenderSnippetAs.HTML_IFRAME,
    renderLinkedObjectsAs: RenderLinkedObjectsAs.VARIATIONS_OF_CURRENT_SYNTAX,
    renderSpecialWikiLinks: true,
    renderLinksWithNofollow: true,
    rootDiv: true,
    anchorHeadings: false,
    chevronLists: false,
    isMobile: true,

    selectPhotoVersionFn: (photo) => photo.versions[0],
    hashizeFn: (bodyChunk) => hashize(bodyChunk, (hash) => url(blogsUrl, {}, { h: hash }), true),
    faFn: null,
};

let postParagraphAppendix = [];

export const astToHtml = (ast: AST, additionalData: FilterAdditionalData, specialFlag: string = null): ReactChild[] => {
    let ret: ReactChild[] = [];
    let i: number = 0;

    if (ast.type === 'document') {
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        if (additionalData.rootDiv) {
            ret = [(<div>{ret}</div>)];
        }
        ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);

    } else if (ast.type === 'paragraph') {
        ret = [];

        let p: ReactChild[] = [];
        let childOfP = true;

        if (ast.elements) {

            // blog paragraphs contain: inline elements, br, headers, table, ...;  does not contain paragraph_line element
            // wiki paragraphs contain: only elements header / paragraph_line / paragraph_endblanks; does not contain br element

            const isBlogParagraph = ast.elements.filter(
                (item) => !['h2', 'h3', 'h4', 'paragraph_line', 'paragraph_endblanks'].includes(item.type),
            ).length > 0;

            if (isBlogParagraph) {
                for (const e of ast.elements) {
                    childOfP = !(
                        e.type === 'table' ||
                        e.type === 'numbered_list' ||
                        e.type === 'unnumbered_list' ||
                        e.type === 'note' ||
                        e.type === 'hr' ||
                        e.type === 'h2' ||
                        e.type === 'h3' ||
                        e.type === 'h4' ||
                        e.type === 'instagram'
                    );

                    if (childOfP) {
                        // if element can be a child of <p>, collect them to the array p and finally append <p>{p}</p> to array ret
                        p.push(...astToHtml(e, additionalData, specialFlag));

                    } else {
                        // if element can't be a child of <p>, then append past elements as <p>{p}</p> to array ret and then append the actual non p element to array ret
                        if (p.length > 0) {
                            p = reactFilter(p, (s: string): React.ReactChild[] => [s]);
                            ret.push((<p className={specialFlag}>{p}</p>));
                            p = [];
                        }
                        ret.push(...astToHtml(e, additionalData, specialFlag));
                    }
                }
                if (p.length > 0) {
                    p = reactFilter(p, (s: string): React.ReactChild[] => [s]);
                    ret.push((<p className={specialFlag}>{p}</p>));
                }

            } else {

                for (i = 0; i < ast.elements.length; i += 1) {
                    const e = ast.elements[i];
                    if (e.type === 'paragraph_line') {
                        if (e.elements) {
                            for (const ee of e.elements) {
                                childOfP = !(
                                    ee.type === 'table' ||
                                    ee.type === 'numbered_list' ||
                                    ee.type === 'unnumbered_list' ||
                                    ee.type === 'note' ||
                                    ee.type === 'hr' ||
                                    ee.type === 'h2' ||
                                    ee.type === 'h3' ||
                                    ee.type === 'h4'
                                );

                                if (childOfP) {
                                    // if element can be a child of <p>, collect them to the array p and finally append <p>{p}</p> to array ret
                                    p.push(...astToHtml(ee, additionalData, specialFlag));

                                } else {
                                    // if element can't be a child of <p>, then append past elements as <p>{p}</p> to array ret and then append the actual non p element to array ret
                                    if (p.length > 0) {
                                        p = reactFilter(p, (s: string): React.ReactChild[] => [s]);
                                        ret.push((<p className={specialFlag}>{p}</p>));
                                        p = [];
                                    }
                                    ret.push(...astToHtml(ee, additionalData, specialFlag));
                                }
                            }

                            // append <br> after each paragraph_line, which ends with element "childable" to <p>
                            if (childOfP) {
                                if (
                                    ast.elements.length - 1 > i + 1 &&
                                    ast.elements[i + 1].type === 'paragraph_line'
                                ) {
                                    p.push((<br />));
                                }
                            }
                        }
                    } else {
                        // append non paragraph_line element (header / paragraph_endblanks) separately

                        ret.push(...astToHtml(e, additionalData, specialFlag));
                    }
                }
                if (p.length > 0) {
                    p = reactFilter(p, (s: string): React.ReactChild[] => [s]);
                    ret.push((<p className={specialFlag}>{p}</p>));
                }
            }

            if (postParagraphAppendix.length > 0) {
                ret.push(...postParagraphAppendix);
                ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
                postParagraphAppendix = [];
            }
        }

    } else if (ast.type === 'paragraph_endblanks') {
        ret = [];

    } else if (ast.type === 'numbered_list') {
        ret = [];
        if (ast.elements) {
            let listItemLev1;
            let listItemsLev2;
            let j = 0;
            for (i = 0; i < ast.elements.length; i += 1) {
                const e = ast.elements[i];
                if (e.type === 'numbered_list_item_lev1') {
                    listItemLev1 = astToHtml(e, additionalData, specialFlag);
                    listItemsLev2 = [];
                    j = 1;
                    while (i + j < ast.elements.length) {
                        if (ast.elements[i + j].type === 'numbered_list_item_lev2') {
                            listItemsLev2.push(
                                <li key={`${i}.${j}`}>
                                    {astToHtml(ast.elements[i + j], additionalData, specialFlag)}
                                </li>,
                            );
                            j += 1;
                        } else {
                            i = i + j - 1;
                            break;
                        }
                    }
                    if (listItemsLev2.length > 0) {
                        listItemsLev2 = (<ol key={`${i}.ol`}>{...listItemsLev2}</ol>);
                        listItemLev1.push(listItemsLev2);
                    }
                    listItemLev1 = (
                        <li key={`${i}`}>
                            {listItemLev1}
                        </li>
                    );
                    ret.push(listItemLev1);
                }
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<ol className={specialFlag}>{ret}</ol>)];

    } else if (ast.type === 'numbered_list_item_lev1') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        // wrap the elements of this node with <li></li> on the level of numbered_list (see above)

    } else if (ast.type === 'numbered_list_item_lev2') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        // wrap the elements of this node with <li>...</li> and <ol>...</ol> on the level of numbered_list (see above)

    } else if (ast.type === 'unnumbered_list') {
        ret = [];
        if (ast.elements) {
            let listItemLev1;
            let listItemsLev2;
            let j = 0;
            for (i = 0; i < ast.elements.length; i += 1) {
                const e = ast.elements[i];
                if (e.type === 'unnumbered_list_item_lev1') {
                    listItemLev1 = astToHtml(e, additionalData, specialFlag);
                    listItemsLev2 = [];
                    j = 1;
                    while (i + j < ast.elements.length) {
                        if (ast.elements[i + j].type === 'unnumbered_list_item_lev2') {
                            listItemsLev2.push(
                                <li key={`${i}.${j}`}>
                                    {astToHtml(ast.elements[i + j], additionalData, specialFlag)}
                                    {additionalData.chevronLists && (
                                        <FontAwesomeIcon icon={faAngleRight} />
                                    )}
                                </li>,
                            );
                            j += 1;
                        } else {
                            i = i + j - 1;
                            break;
                        }
                    }
                    if (listItemsLev2.length > 0) {
                        listItemsLev2 = (<ul key={`${i}.ul`}>{...listItemsLev2}</ul>);
                        listItemLev1.push(listItemsLev2);
                    }
                    listItemLev1 = (
                        <li key={`${i}`}>
                            {listItemLev1}
                            {additionalData.chevronLists && (
                                <FontAwesomeIcon icon={faAngleRight} />
                            )}
                        </li>
                    );
                    ret.push(listItemLev1);
                }
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<ul className={specialFlag}>{ret}</ul>)];

    } else if (ast.type === 'unnumbered_list_item_lev1') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        // wrap the elements of this node with <li></li> on the level of unnumbered_list (see above)

    } else if (ast.type === 'unnumbered_list_item_lev2') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        // wrap the elements of this node with <li>...</li> and <ul>...</ul> on the level of unnumbered_list (see above)

    } else if (ast.type === 'table') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<table>{ret}</table>)];

    } else if (ast.type === 'table_row') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<tbody><tr>{ret}</tr></tbody>)];

    } else if (ast.type === 'table_header_cell') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<th>{ret}</th>)];

    } else if (ast.type === 'table_cell') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<td>{ret}</td>)];

    } else if (ast.type === 'note') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [
            (
                <p className="article-note">{ret}</p>
            ),
        ];

    } else if (ast.type === 'resources') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, 'article-resources'));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }

    } else if (ast.type === 'h2') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<h2 className={specialFlag} id={additionalData.anchorHeadings ? slugifyContent(ret) : null}>{ret}</h2>)];

    } else if (ast.type === 'h3') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<h3 className={specialFlag} id={additionalData.anchorHeadings ? slugifyContent(ret) : null}>{ret}</h3>)];

    } else if (ast.type === 'h4') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<h4 className={specialFlag} id={additionalData.anchorHeadings ? slugifyContent(ret) : null}>{ret}</h4>)];

    } else if (ast.type === 'bold') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<b>{ret}</b>)];

    } else if (ast.type === 'italic') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<i>{ret}</i>)];

    } else if (ast.type === 'code') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...astToHtml(e, additionalData, specialFlag));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<code>{ret}</code>)];

    } else if (ast.type === 'link') {
        ret = linkToHtml(ast, additionalData, specialFlag);

    } else if (ast.type === 'fa') {
        if (additionalData.faFn) {
            const faElem = additionalData.faFn(ast.elements[0].text);
            if (faElem) {
                ret = [faElem];
            }
        }

    } else if (ast.type === 'pic') {
        if (!additionalData.ignorePhotosAndVideos) {
            const attributes = getAttributesDict(ast);
            const cd = removeQuotes(attributes.hasOwnProperty('code')
                ? attributes.code
                : attributes.hasOwnProperty('cd')
                    ? attributes.cd
                    : '');
            const align = removeQuotes(attributes.hasOwnProperty('align') ? attributes.align : 'center');
            const size = removeQuotes(attributes.hasOwnProperty('size') ? attributes.size : '');
            const title = removeQuotes(attributes.hasOwnProperty('title') ? attributes.title : '');
            const caption = removeQuotes(attributes.hasOwnProperty('caption') ? attributes.caption : '');
            const linkAddress = removeQuotes(attributes.hasOwnProperty('link_address') ? attributes.link_address : '');
            const isAvatar = attributes.hasOwnProperty('avatar');
            const photo = additionalData.photos.filter((p) => p.code === cd);

            if (photo.length === 0) {
                ret = [];
            } else {

                let version = '';
                if (size === '' && photo[0].versions && photo[0].versions.length > 0) {
                    version = additionalData.selectPhotoVersionFn(photo[0]);
                }

                let imgElement: ReactChild[] = [(
                    <span className={additionalData.photoStyle}>
                        {constructImage(
                            additionalData.photos,
                            cd,
                            align,
                            size,
                            version,
                            title,
                            isAvatar,
                            additionalData.hasResponsiveImages,
                            additionalData.forceEagerImages,
                        )}
                    </span>
                )];

                if (linkAddress) {
                    imgElement = reactFilter(imgElement, (s: string): React.ReactChild[] => [s]);
                    const linkprocessorOpts = specialFlag === 'article-resources' ? {nofollow: false} : undefined;
                    imgElement = [linkProcessor(linkAddress, imgElement, additionalData.countTopicContributors, linkprocessorOpts)];
                }
                ret = reactFilter(imgElement, (s: string): React.ReactChild[] => [s]);

                if (caption) {
                    if (Array.isArray(postParagraphAppendix)) {
                        postParagraphAppendix.push((<p className="article-note">{caption}</p>));
                    } else {
                        postParagraphAppendix = [(<p className="article-note">{caption}</p>)];
                    }
                }
            }
        }
    } else if (ast.type === 'iframe') {
        const attributes = getAttributesDict(ast);
        const src = removeQuotes(attributes.hasOwnProperty('src') ? attributes.src : '');
        const desktopHeight = attributes.hasOwnProperty('desktop-height') ? parseInt(removeQuotes(attributes['desktop-height']), 10) : null;
        const mobileHeight = attributes.hasOwnProperty('mobile-height') ? parseInt(removeQuotes(attributes['mobile-height']), 10) : null;
        ret = [constructIframe(src, additionalData.isMobile ? mobileHeight : desktopHeight, additionalData.renderSnippetObjectsAs)];
    } else if (['youtube', 'vimeo', 'giphy', 'instagram'].includes(ast.type)) {
        if (!additionalData.ignorePhotosAndVideos) {
            ret = [];
            const attributes = getAttributesDict(ast);
            const code = removeQuotes(attributes.hasOwnProperty('code') ? attributes.code : '');
            const width = removeQuotes(attributes.hasOwnProperty('width') ? attributes.width : '');
            const height = removeQuotes(attributes.hasOwnProperty('height') ? attributes.height : '');

            if (ast.type === 'youtube') {
                const youtubeElement = constructYoutube(
                    code,
                    width && height ? parseInt(width, 10) : 560,
                    width && height ? parseInt(height, 10) : 315,
                    additionalData.renderSnippetObjectsAs,
                );
                ret = [youtubeElement];
            }
            if (ast.type === 'vimeo') {
                const vimeoElement = constructVimeo(
                    code,
                    width && height ? parseInt(width, 10) : 560,
                    width && height ? parseInt(height, 10) : 315,
                    additionalData.renderSnippetObjectsAs,
                );
                ret = [vimeoElement];
            }
            if (ast.type === 'instagram') {

                const instagramData = additionalData.instagramData && additionalData.instagramData[code]
                    ? additionalData.instagramData[code]
                    : null;

                const instagramElement: ReactChild[] = [(
                    <div className={additionalData.instagramStyle}>
                        {constructInstagram(
                            code,
                            height ? parseInt(height, 10) : undefined,
                            additionalData.renderSnippetObjectsAs,
                            instagramData,
                        )}
                    </div>
                )];
                ret = reactFilter(instagramElement, (s: string): React.ReactChild[] => [s]);
            }
            if (ast.type === 'giphy') {
                const giphyElement = constructGiphy(
                    code,
                    width && height ? parseInt(width, 10) : 560,
                    width && height ? parseInt(height, 10) : 315,
                    additionalData.renderSnippetObjectsAs,
                );
                ret = [giphyElement];
            }
        }
    } else if (ast.type === 'plain_text') {
        ret = [];
        if (ast.text) {
            ret = [ast.text];
        }

    } else if (ast.type === 'hashtag') {
        ret = [];
        if (ast.text) {
            ret = additionalData.hashizeFn([ast.text]);
        }

    } else if (ast.type === 'mention') {
        ret = [];
        if (ast.text) {
            ret = mentionize(ast.text);
        }

    } else if (ast.type === 'hr') {
        ret.push(<hr />);

    } else if (ast.type === 'br') {
        ret.push(<br />);

    } else if (ast.type === 'span') {
        // ignore

    } else if (ast.type === 'attribute') {
        // ignore, should not handle here

    } else {
        throw new Error(`unknown node type in the ast - ${ast.type}`);
    }

    return ret;
};

export const linkToHtml = (ast: AST, additionalData: FilterAdditionalData, specialFlag: string = null): ReactChild[] => {
    let ret: ReactChild[] = [];

    const plainTextNode = ast.elements.find((e) => e.type === 'plain_text');
    const plainTextText = plainTextNode ? plainTextNode.text : '';

    const linkToObjectNode = ast.elements.find((e) => e.type === 'link_to_object');
    const objType = linkToObjectNode ? linkToObjectNode.obj_type : '';
    const objId = linkToObjectNode ? linkToObjectNode.obj_id : null;
    const objQOH = linkToObjectNode ? (linkToObjectNode.obj_qoh || '') : null; // link's query/hash part

    if (
        objType && objId && (
            !Object.keys(additionalData.linkedObjects).includes(objType) ||
            !Object.keys(additionalData.linkedObjects[objType]).includes(objId.toString())
        )
    ) {
       return [];
    }

    if (objType && objId && additionalData.linkedObjects && additionalData.linkedObjects[objType]) {
        if (objType === ASTLinkedObjectType.STROLLERS_STROLLER_DETAIL) {
            const objData = additionalData.linkedObjects[objType][objId];
            if (objData) {
                if (plainTextText) {
                    const strollersDetailUrlRe = path2regex(strollersDetailUrl);
                    const parsedUrl = strollersDetailUrlRe.exec(objData.url);
                    ret = [(
                        <DetailLink
                            key={`${objType}-${objId}`}
                            slug={parsedUrl[1]}
                            visibleText={plainTextText || objData.title}
                        />
                    )];
                } else {
                    ret = [(
                        <Link to={objData.url + objQOH}>
                            {plainTextText || BASE_URL + objData.url + objQOH}
                        </Link>
                    )];
                }
            }
        }
        if (objType === ASTLinkedObjectType.STROLLERS_STROLLER_BRAND) {
            const objData = additionalData.linkedObjects[objType][objId];
            if (objData) {
                if (plainTextText) {
                    const strollersListUrlRe = path2regex(strollersListUrl);
                    const parsedUrl = strollersListUrlRe.exec(objData.url);
                    ret = [(
                        <BrandLink
                            key={`${objType}-${objId}`}
                            slug={parsedUrl[1]}
                            visibleText={plainTextText || objData.title}
                        />
                    )];
                } else {
                    ret = [(
                        <Link to={objData.url + objQOH}>
                            {plainTextText || BASE_URL + objData.url + objQOH}
                        </Link>
                    )];
                }
            }
        }
        if (objType === ASTLinkedObjectType.WIKI_ARTICLE) {
            const objData = additionalData.linkedObjects[objType][objId];
            if (objData) {
                if (additionalData.renderSpecialWikiLinks && plainTextText) {
                    ret = [(
                        <WikiLink
                            key={`${objType}-${objId}`}
                            link={objData.url + objQOH}
                            visibleText={plainTextText || objData.title}
                            count={additionalData.renderForEditor ? 0 : objData.countExperiences}
                        />
                    )];
                } else {
                    ret = [(
                        <Link to={objData.url + objQOH}>
                            {plainTextText || BASE_URL + objData.url + objQOH}
                        </Link>
                    )];
                }
            }
        }
        if (objType === ASTLinkedObjectType.PHOTOBLOG_ARTICLE) {
            const objData = additionalData.linkedObjects[objType][objId];
            if (objData) {
                ret = [(
                    <Link to={objData.url}>
                        {plainTextText || BASE_URL + objData.url + objQOH}
                    </Link>
                )];
            }
        }
        if (objType === ASTLinkedObjectType.FORUM_POST_DETAIL) {
            const objData = additionalData.linkedObjects[objType][objId];
            if (objData) {
                ret = [(
                    <Link to={objData.url}>
                        {plainTextText || BASE_URL + objData.url + objQOH}
                    </Link>
                )];
            }
        }
    } else {
        const linkAddressNode = ast.elements.find((e) => e.type === 'link_address');
        let linkAddressText = linkAddressNode ? linkAddressNode.text : '';

        const attributes = getAttributesDict(ast);
        if (attributes.hasOwnProperty('link_address')) {
            linkAddressText = removeQuotes(attributes.link_address);
        }

        let linkOpts: LinkProcessorOpts;
        linkOpts = {nofollow: additionalData.renderLinksWithNofollow};

        /* disabled cause nofollow=false is currently default for wiki articles, see apps/wiki/../ArticleBody.tsx
         * if (specialFlag === 'article-resources') {
         *     linkOpts = {nofollow: false};
         * }
         */

        if (linkAddressText && linkAddressText.startsWith(BASE_URL)) {
            linkOpts.target = false;
        }

        if (linkAddressText !== '' && plainTextText !== '') {
            ret = [linkProcessor(
                linkAddressText,
                plainTextText,
                additionalData.countTopicContributors,
                linkOpts,
                specialFlag === 'article-resources' && additionalData.renderContributorsCount,
            )];
        } else if (linkAddressText !== '') {
            ret = urlize(linkAddressText, specialFlag === 'article-resources' ? 100 : 50, linkOpts);
        }
    }
    return ret;
};

const REMOVE_PREV_NEWLINE = '#_REMOVE_PREV_NEWLINE_#';

const slugifyContent = (nodes: React.ReactChild[]): string => {
    const firstTextNode = nodes.find((node) => typeof node === 'string');
    return firstTextNode ? 'h-' + slugify(firstTextNode as string) : null;
};

// arrays holding markdown ending/starting strings
// applicable in case of <br> to interupt the formatting and start again on the new line
// to convert '<b>abc<br>def</b>' to '**abc**\n**def**'
const markdownStackEnd = [];
const markdownStackStart = [];

export const astToMarkdown = (treeNode: AST, additionalData: FilterAdditionalData, ignoreAttribute = true): string => {
    let result = '';
    const hasType = 'type' in treeNode;
    const hasElements = 'elements' in treeNode && !!treeNode.elements && treeNode.elements.length > 0;
    const hasText = 'text' in treeNode && treeNode.text != null;

    if (hasType) {
        if (treeNode.type === '') {
            return null;

        //////////////////////////////
        // Document nodes
        } else if (treeNode.type === 'document') {
            result += ''; // beginning
            for (const subNode of treeNode.elements) {
                result += astToMarkdown(subNode, additionalData);
            }
            result = result.replace('\n' + REMOVE_PREV_NEWLINE, '').replace(REMOVE_PREV_NEWLINE, '');
            result += ''; // end

        } else if (treeNode.type === 'paragraph') {
            if (hasElements) {
                result += ''; // beginning
                let containParagraphLine = false;
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                    if (subNode.type === 'paragraph_line') {
                        containParagraphLine = true;
                    }
                }
                if (!containParagraphLine) {
                    result += '\n'; // add extra new line after a paragraph where is no paragraph line (html articles)
                }
            }
            result += '\n';

        } else if (treeNode.type === 'paragraph_line') {
            if (hasElements) {
                result += ''; // beginning
                let containComplexElement = false;
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                    if (
                        subNode.type === 'table' ||
                        subNode.type === 'numbered_list' ||
                        subNode.type === 'unnumbered_list' ||
                        subNode.type === 'note'
                    ) {
                        containComplexElement = true;
                    }
                }
                if (!containComplexElement) {
                    result += '\n';  // add extra new line after a paragraph where is no table or list
                }
            }

        } else if (treeNode.type === 'paragraph_endblanks') {
            if (hasText) {
                // render trailing useless blank lines of the paragraph
                // result += treeNode['text']

                // ignore trailing useless blank lines of the paragraph
                result += '';
            } else {
                // assert false  # missing text attribute
            }

        //////////////////////////////
        // Complex elements

        } else if (treeNode.type === 'numbered_list') {
            if (hasElements) {
                result += ''; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += ''; // end
            }

        } else if (treeNode.type === 'numbered_list_item_lev1') {
            if (hasElements) {
                result += '-# '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += '\n'; // end
            }

        } else if (treeNode.type === 'numbered_list_item_lev2') {
            if (hasElements) {
                result += '-## '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += '\n'; // end
            }

        } else if (treeNode.type === 'unnumbered_list') {
            if (hasElements) {
                result += ''; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += ''; // end
            }

        } else if (treeNode.type === 'unnumbered_list_item_lev1') {
            if (hasElements) {
                result += '- '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += '\n'; // end
            }

        } else if (treeNode.type === 'unnumbered_list_item_lev2') {
            if (hasElements) {
                result += '-- '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += '\n'; // end
            }

        } else if (treeNode.type === 'table' && hasElements) {
            result += ''; // beginning
            for (const subNode of treeNode.elements) {
                result += astToMarkdown(subNode, additionalData);
            }
            result += ''; // end

        } else if (treeNode.type === 'table_row' && hasElements) {
            const tHCells = getAllDescendantsOfAType(treeNode, 'table_header_cell', 1);
            const tDCells = getAllDescendantsOfAType(treeNode, 'table_cell', 1);
            if (tDCells.length + tHCells.length) {
                result += '|'; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += '\n'; // end
            }

        } else if (treeNode.type === 'table_header_cell') {
            result += '='; // beginning
            if (hasElements) {
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
            }
            result += '|'; // end

        } else if (treeNode.type === 'table_cell') {
            result += ''; // beginning
            if (hasElements) {
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
            }
            result += '|'; // end

        } else if (treeNode.type === 'note') {
            if (hasElements) {
                result += ':::: '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += '\n'; // end
            }

        } else if (treeNode.type === 'resources') {
            if (hasElements) {
                result += '++++\n'; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
                result += REMOVE_PREV_NEWLINE + '++++\n\n'; // end
            }

        //////////////////////////////
        // Rich text nodes / leaves

        } else if (treeNode.type === 'h2') {
            if (hasElements) {
                result += '== '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
            }
            result += '\n'; // end

        } else if (treeNode.type === 'h3') {
            if (hasElements) {
                result += '=== '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
            }
            result += '\n'; // end

        } else if (treeNode.type === 'h4') {
            if (hasElements) {
                result += '==== '; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(subNode, additionalData);
                }
            }
            result += '\n'; // end

        } else if (treeNode.type === 'bold') {
            if (hasElements) {
                result += '**'; // beginning
                markdownStackEnd.push('**');
                markdownStackStart.push('**');
                if (hasElements) {
                    for (const subNode of treeNode.elements) {
                        result += astToMarkdown(subNode, additionalData);
                    }
                }
                markdownStackEnd.pop();
                markdownStackStart.pop();
                result += '**'; // end
            }

        } else if (treeNode.type === 'italic') {
            if (hasElements) {
                result += '//'; // beginning
                markdownStackEnd.push('//');
                markdownStackStart.push('//');
                if (hasElements) {
                    for (const subNode of treeNode.elements) {
                        result += astToMarkdown(subNode, additionalData);
                    }
                }
                markdownStackEnd.pop();
                markdownStackStart.pop();
                result += '//'; // end
            }

        } else if (treeNode.type === 'code') {
            if (hasElements) {
                result += '{{{'; // beginning
                for (const subNode of treeNode.elements) {
                    result += astToMarkdown(
                        subNode,
                        {...additionalData, escapeMarkdownLikePartsOfPlainText: false},
                        true,
                    );
                }
                result += '}}}'; // end
            }

        } else if (treeNode.type === 'link') {
            if (hasElements) {
                let linkAddress = '';
                let wikiLinkWithCategory = '';
                let description = '';
                let objType = null;
                let objId = null;
                for (const subNode of treeNode.elements) {
                    if ('type' in subNode && subNode.type === 'attribute') {
                        const attributeName = subNode.elements.find((e) => e.type === 'attribute_name').text;
                        const attributeValue = subNode.elements.find((e) => e.type === 'attribute_value').text;
                        if (attributeName === 'link_address') {
                            linkAddress = removeQuotes(attributeValue);
                        }
                    }
                    if ('type' in subNode && subNode.type === 'link_address') {
                        linkAddress = subNode.text;
                    }
                    if ('type' in subNode && subNode.type === 'link_to_object') {
                        objType = subNode.obj_type;
                        objId = subNode.obj_id;

                        if (
                            !Object.keys(additionalData.linkedObjects).includes(objType) ||
                            !Object.keys(additionalData.linkedObjects[objType]).includes(objId.toString())
                        ) {
                            linkAddress = '';
                            objId = null;
                        } else {
                            const objUrl = additionalData.linkedObjects[objType][objId].url;
                            const objQOH = subNode.obj_qoh || '';
                            const reMatch = linkAddress.match(RE_EXTRACT_ARTICLE_SLUG);
                            linkAddress = `${objUrl}${objQOH}`;
                            wikiLinkWithCategory = `${objUrl}${objQOH}`;
                            if (reMatch) {
                                const slug = reMatch[1];
                                linkAddress = `/${slug}/${objQOH}`;
                            }
                        }
                    }
                    if ('type' in subNode && subNode.type === 'plain_text') {
                        description = subNode.text;
                    }
                }

                // render as markdown
                // [[/article-slug/|Description]]
                // [[/article-slug/#optional-tag|Description]]
                if (additionalData.renderLinkedObjectsAs === RenderLinkedObjectsAs.VARIATIONS_OF_CURRENT_SYNTAX) {
                    if (linkAddress !== '' && description !== '') {
                        result += '[[' + linkAddress + '|' + description + ']]';
                    } else if (linkAddress !== '' && description === '') {
                        result += !objId ? linkAddress : BASE_URL + wikiLinkWithCategory;
                    } else if (linkAddress === '' && description !== '') {
                        // assert false  # missing linkAddress
                        result += '[[' + '' + '|' + description + ']]';
                    }

                // render as plain link
                // https://www.modrykonik.sk/category-slug/article-slug/
                // https://www.modrykonik.sk/category-slug/article-slug/#optional-tag
                } else if (additionalData.renderLinkedObjectsAs === RenderLinkedObjectsAs.PLAIN_URL_LINK) {
                    result += !objId ? linkAddress : BASE_URL + wikiLinkWithCategory;
                } else {
                    // assert false
                }
            }

        } else if (treeNode.type === 'pic') {
            if (hasElements) {
                result += '<<pic'; // beginning
                let attributes = '';
                for (const subNode of treeNode.elements) {
                    if ('type' in subNode && subNode.type === 'attribute') {
                        attributes += astToMarkdown(subNode, additionalData, false);
                    }
                }
                if (!attributes.includes(' title="')) {
                    attributes += ' title=""';
                }
                result += attributes;
                result += '>>'; // end
            }

        } else if (treeNode.type === 'iframe') {
            if (hasElements) {
                result += '<<iframe'; // beginning
                let attributes = '';
                for (const subNode of treeNode.elements) {
                    if ('type' in subNode && subNode.type === 'attribute') {
                        attributes += astToMarkdown(subNode, additionalData, false);
                    }
                }
                result += attributes;
                result += '>>'; // end
            }

        } else if (treeNode.type === 'youtube') {
            if (hasElements) {
                if (additionalData.renderSnippetObjectsAs === RenderSnippetAs.MARKDOWN) {
                    result += '<<youtube'; // beginning
                    for (const subNode of treeNode.elements) {
                        result += astToMarkdown(subNode, additionalData, false);
                    }
                    result += '>>'; // end
                } else {
                    // else (by default) RenderSnippetAs.PLAIN_LINK
                    const attributes = getAttributesDict(treeNode);
                    const code = removeQuotes(attributes.hasOwnProperty('code') ? attributes.code : '');
                    result += constructYoutube(code, 0, 0, RenderSnippetAs.PLAIN_LINK);
                }
            }

        } else if (treeNode.type === 'vimeo') {
            if (hasElements) {
                if (additionalData.renderSnippetObjectsAs === RenderSnippetAs.MARKDOWN) {
                    result += '<<vimeo'; // beginning
                    for (const subNode of treeNode.elements) {
                        result += astToMarkdown(subNode, additionalData, false);
                    }
                    result += '>>'; // end
                } else {
                    // else (by default) RenderSnippetAs.PLAIN_LINK
                    const attributes = getAttributesDict(treeNode);
                    const code = removeQuotes(attributes.hasOwnProperty('code') ? attributes.code : '');
                    result += constructVimeo(code, 0, 0, RenderSnippetAs.PLAIN_LINK);
                }
            }

        } else if (treeNode.type === 'giphy') {
            if (hasElements) {
                if (additionalData.renderSnippetObjectsAs === RenderSnippetAs.MARKDOWN) {
                    result += '<<giphy'; // beginning
                    for (const subNode of treeNode.elements) {
                        result += astToMarkdown(subNode, additionalData, false);
                    }
                    result += '>>'; // end
                } else {
                    // else (by default) RenderSnippetAs.PLAIN_LINK
                    const attributes = getAttributesDict(treeNode);
                    const code = removeQuotes(attributes.hasOwnProperty('code') ? attributes.code : '');
                    result += constructGiphy(code, 0, 0, RenderSnippetAs.PLAIN_LINK);
                }
            }

        } else if (treeNode.type === 'instagram') {
            if (hasElements) {
                if (additionalData.renderSnippetObjectsAs === RenderSnippetAs.MARKDOWN) {
                    result += '<<instagram'; // beginning
                    for (const subNode of treeNode.elements) {
                        result += astToMarkdown(subNode, additionalData, false);
                    }
                    result += '>>'; // end
                } else {
                    // else (by default) RenderSnippetAs.PLAIN_LINK
                    const attributes = getAttributesDict(treeNode);
                    const code = removeQuotes(attributes.hasOwnProperty('code') ? attributes.code : '');
                    result += constructInstagram(code, 0, RenderSnippetAs.PLAIN_LINK);
                }
            }

        } else if (treeNode.type === 'attribute') {
            if (hasElements && !ignoreAttribute) {
                let attributeName = '';
                let attributeValue = '';
                for (const subNode of treeNode.elements) {
                    if ('type' in subNode && subNode.type === 'attribute_name') {
                        attributeName = 'text' in subNode ? subNode.text : '';
                    }
                    if ('type' in subNode && subNode.type === 'attribute_value') {
                        attributeValue = 'text' in subNode ? subNode.text : '';
                    }
                }
                if (attributeName !== '' && attributeValue === BOOLEAN_ATTRIBUTE__TRUE) {
                    result += ' ' + attributeName;
                } else if (attributeName !== '' && attributeValue !== '') {
                    if (!attributeValue.startsWith('"') || !attributeValue.endsWith('"')) {
                        attributeValue = '"' + attributeValue + '"';
                    }
                    result += ' ' + attributeName + '=' + attributeValue;
                } else {
                    // assert false  # missing attribute_name or attribute_value
                }
            }

        } else if (treeNode.type === 'plain_text') {
            if (hasText) {
                let text = treeNode.text;
                // escape plain text containing markdown patterns
                if (additionalData.escapeMarkdownLikePartsOfPlainText) {
                    text = text.replace(/((?:[^{]|^){3})(\*\*)((?:[^{]|$){3})/, '$1{{{$2}}}$3');
                    text = text.replace(/((?:[^{]|^){3})(\/\/)((?:[^{]|$){3})/, '$1{{{$2}}}$3');
                    text = text.replace(/((?:[^{]|^){3})(\[\[)((?:[^{]|$){3})/, '$1{{{$2}}}$3');
                    text = text.replace(/((?:[^{]|^){3})(]])((?:[^{]|$){3})/, '$1{{{$2}}}$3');
                    text = text.replace(/((?:[^{]|^){3})(\|)((?:[^{]|$){3})/, '$1{{{$2}}}$3');
                    text = text.replace(/((?:[^{]|^){3})(====)((?:[^{]|$){3})/, '$1{{{====}}}$3');
                    text = text.replace(/((?:[^{=]|^){3})(===)((?:[^{=]|$){3})/, '$1{{{===}}}$3');
                    text = text.replace(/((?:[^{=]|^){3})(==)((?:[^{=]|$){3})/, '$1{{{==}}}$3');
                    text = text.replace(/((?:[^{]|^){3})(<<pic.*?>>)((?:[^{]|$){3})/, '$1{{{$2}}}$3');
                    text = text.replace(/((?:[^{]|^){3})(<<youtube.*?>>)((?:[^{]|$){3})/, '$1{{{$2}}}$3');
                }
                result += text;
            } else {
                // assert false  # missing text attribute
            }

        } else if (treeNode.type === 'hashtag') {
            if (hasText) {
                result += treeNode.text;
            } else {
                // assert false  # missing text attribute
            }

        } else if (treeNode.type === 'mention') {
            if (hasText) {
                result += treeNode.text;
            } else {
                // assert false  # missing text attribute
            }

        } else if (treeNode.type === 'hr') {
            result += '---';

        } else if (treeNode.type === 'br') {
            let endingOfActualFormatting = '';
            let startingOfActualFormatting = '';
            for (const mark of markdownStackEnd) {
                endingOfActualFormatting = mark + endingOfActualFormatting;
            }
            for (const mark of markdownStackStart) {
                startingOfActualFormatting = startingOfActualFormatting + mark;
            }
            result += endingOfActualFormatting + '\n' + startingOfActualFormatting;

        } else {
            // if the code reaches here, it means something is wrong - the AST contains unknown node type
            // print('---- Missing ' + treeNode['type'])
            // assert false  # unknown node type
        }

    } else {
        // if the code reaches here, it means something is wrong - the AST node has no type
        // assert false  # missing node type
    }

    return result;
};
